import { Box, Flex, VStack } from '@chakra-ui/react'
import { Mic, Speaker, VideoCamera, VideoCameraOff } from '@components/icons'
import { useDevices, useLocalSessionId } from '@daily-co/daily-react'
import { useExperienceManager } from '@hooks'
import React from 'react'
import { AVDeviceSelector } from './av-device-selector'
import { ParticipantTile } from './participant-tile'
import { SettingsActionBar } from './settings-action-bar'

type CallSetupProps = {
  bottomActionBar?: React.ReactNode
  maskInputs?: boolean
  padding?: string
}

const DEFAULT_SAFARI_SPEAKER = {
  device: {
    deviceId: 'default',
    label: 'System Default',
    kind: 'speaker',
    groupId: 'default'
  }
}

export const CallSetup = ({
  bottomActionBar,
  maskInputs,
  padding
}: CallSetupProps) => {
  const { isSafari } = useExperienceManager()
  const devices = useDevices()
  const localId = useLocalSessionId()

  const handleMicInputChange = (event: any) => {
    devices.setMicrophone(event.target.value)
  }

  const handleCamInputChange = (event: any) => {
    devices.setCamera(event.target.value)
  }

  const handleSpeakerInputChange = (event: any) => {
    devices.setSpeaker(event.target.value)
  }

  return (
    <Box
      minW="300px"
      maxW="500px"
      width="100%"
      bg="white"
      m="auto"
      mt="small"
      borderRadius="12px"
      p={padding || '0px'}
    >
      <Flex
        borderRadius="12px"
        bg="black"
        justify="center"
        align="center"
        w="100%"
        h="100%"
      >
        <Box
          borderRadius="12px"
          h={maskInputs ? '263px' : 'auto'}
          w="100%"
          overflow="hidden"
          position="relative"
        >
          {maskInputs ? (
            <Flex
              position="absolute"
              top="0"
              left="0"
              width="100%"
              height="100%"
              justify="center"
              align="center"
              bg="gray.800"
              borderRadius="12px"
            >
              <VideoCameraOff width="80px" fill="#757575" />
            </Flex>
          ) : (
            <ParticipantTile
              borderRadius="0px"
              participantId={localId}
              aspectRatio={16 / 9}
              setAspectRatio={() => {}}
              videoFitOverride="contain"
            />
          )}
        </Box>
      </Flex>
      <Box w="100%" h="100%" position="relative" mt="xsmall">
        {maskInputs && (
          <Box
            zIndex={1000}
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="white"
            opacity="0.8"
          />
        )}
        <SettingsActionBar />
        <VStack spacing={3}>
          <AVDeviceSelector
            type="camera"
            icon={<VideoCamera width="24px" />}
            devices={devices.cameras}
            selectedDeviceId={devices.currentCam?.device.deviceId || ''}
            onDeviceChange={handleCamInputChange}
            isBlocked={devices.hasCamError}
          />
          <AVDeviceSelector
            type="microphone"
            icon={<Mic width="24px" />}
            devices={devices.microphones}
            selectedDeviceId={devices.currentMic?.device.deviceId || ''}
            onDeviceChange={handleMicInputChange}
            isBlocked={devices.hasMicError}
          />
          <AVDeviceSelector
            type="speaker"
            icon={<Speaker width="24px" />}
            devices={isSafari ? [DEFAULT_SAFARI_SPEAKER] : devices.speakers}
            selectedDeviceId={
              devices.currentSpeaker?.device.deviceId || 'default'
            }
            onDeviceChange={handleSpeakerInputChange}
            isBlocked={devices.hasMicError}
          />
        </VStack>
      </Box>
      <Box mt="small">{bottomActionBar}</Box>
    </Box>
  )
}
